/**
 * Resizes an image file using the Canvas API (client-side, no extra packages).
 * - mainFile: original resized to ≤800px wide (unchanged if already ≤800px)
 * - thumbFile: always 250px wide (or original width if smaller), with 'x250' suffix before extension
 */
export async function createResizedPair(
  file: File
): Promise<{ mainFile: File; thumbFile: File }> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(objectUrl);

      const origW = img.width;
      const origH = img.height;
      const mimeType = file.type || 'image/jpeg';

      // Main: resize to 800px wide if original is wider
      const mainW = origW > 800 ? 800 : origW;
      const mainH = Math.round(origH * (mainW / origW));

      const mainCanvas = document.createElement('canvas');
      mainCanvas.width = mainW;
      mainCanvas.height = mainH;
      mainCanvas.getContext('2d')!.drawImage(img, 0, 0, mainW, mainH);

      // Thumb: 250px wide (don't upscale if image is smaller)
      const thumbW = Math.min(origW, 250);
      const thumbH = Math.round(origH * (thumbW / origW));

      const thumbCanvas = document.createElement('canvas');
      thumbCanvas.width = thumbW;
      thumbCanvas.height = thumbH;
      thumbCanvas.getContext('2d')!.drawImage(img, 0, 0, thumbW, thumbH);

      // Derive file names
      const dotIdx = file.name.lastIndexOf('.');
      const baseName = dotIdx > -1 ? file.name.slice(0, dotIdx) : file.name;
      const extPart = dotIdx > -1 ? file.name.slice(dotIdx) : '.jpg';

      let mainDone = false;
      let thumbDone = false;
      let mainFile: File;
      let thumbFile: File;

      mainCanvas.toBlob(
        blob => {
          if (!blob) { reject(new Error('Main canvas toBlob failed')); return; }
          mainFile = new File([blob], `${baseName}${extPart}`, { type: mimeType });
          mainDone = true;
          if (thumbDone) resolve({ mainFile, thumbFile: thumbFile! });
        },
        mimeType,
        0.85
      );

      thumbCanvas.toBlob(
        blob => {
          if (!blob) { reject(new Error('Thumb canvas toBlob failed')); return; }
          thumbFile = new File([blob], `${baseName}x250${extPart}`, { type: mimeType });
          thumbDone = true;
          if (mainDone) resolve({ mainFile: mainFile!, thumbFile });
        },
        mimeType,
        0.80
      );
    };

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error('Image load failed'));
    };

    img.src = objectUrl;
  });
}
